import React from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import Checkpoint from "models/checkpoint";
import {
	useCheckpointsCapacities,
	useEventConfigCapacities
} from "../../../../utils/rights";
import CheckpointsTable from "./CheckpointsTable";
import Style from "./CheckpointsList.module.scss";
import PageSizeDropdown from "../../../../components/page-size-dropdown";
import { PAGE_SIZE_OPTIONS } from "../../constants";
import ChecklistsBatchActions from "./batch-actions/checklists-batch-actions";
import useEventList from "api/api-hooks/useEventList";

const CheckpointsList = ({
	checkpoints,
	ordering,
	toggleOrdering,
	changeCheckpointsStatus,
	exportAttendeesList,
	createExport,
	duplicateCheckpoint,
	organization,
	pagination,
	loading,
	selectedCheckpointIds,
	pageSize,
	refetch
}) => {
	const { t } = useTranslation();

	const { canEdit, canAdd } = useCheckpointsCapacities(organization.id());
	const { canManage } = useEventConfigCapacities(organization.id());

	const { data: events } = useEventList({
		params: {
			with_config: true
		}
	});

	/**
	 * @function updateSelected
	 * @description update all the checkpoints selected inside the table
	 * @param {integer} action - action: active (1), archive (2), inactive (0)
	 */
	const changeSelectedCheckpointsStatus = status => {
		changeCheckpointsStatus({
			status,
			checkpoints: checkpoints.filter(checkpoint =>
				selectedCheckpointIds.value.includes(checkpoint.id())
			)
		});

		// clean checkpoints selected and allSelected checkbox
		selectedCheckpointIds.onChange([]);
	};

	const createExportForSelectedCheckpoints = process => {
		createExport(
			checkpoints.filter(c =>
				selectedCheckpointIds.value.includes(c.id())
			),
			process
		);
	};

	/**
	 * @function getSelectedChecklistsStatuses
	 * @description Returns the statuses of the selected checkpoints
	 * @returns {Array} List of statuses of the selected checkpoints
	 */
	const selectedChecklistsStatuses = React.useMemo(() => {
		return checkpoints
			?.filter(checkpoint =>
				selectedCheckpointIds.value.includes(checkpoint.id())
			)
			.map(checkpoint => checkpoint.status());
	}, [checkpoints, selectedCheckpointIds.value]);

	const handleSuccessBatchAction = () => {
		refetch();
		selectedCheckpointIds.onChange([]);
	};

	return (
		<>
			<div className={Style["flex-justify"]}>
				<ChecklistsBatchActions
					selectedCheckpoints={selectedCheckpointIds.value}
					selectedCheckpointsStatuses={selectedChecklistsStatuses}
					canEdit={canEdit}
					onChangeStatus={changeSelectedCheckpointsStatus}
					onExport={createExportForSelectedCheckpoints}
					onSuccess={handleSuccessBatchAction}
					canManage={canManage}
					hasEvents={!!events?.length}
				/>
				<PageSizeDropdown
					pageSize={pageSize.value}
					onChange={pageSize.onChange}
					pageSizeOptions={PAGE_SIZE_OPTIONS}
					labelRender={value =>
						t("access.checkpoint.pageSizeDropdownLabel", { value })
					}
				/>
			</div>
			<CheckpointsTable
				data={checkpoints}
				rowSelection={{
					selected: selectedCheckpointIds.value,
					onChangeSelection: selectedCheckpointIds.onChange
				}}
				onChangeOrdering={toggleOrdering}
				ordering={ordering}
				canEdit={canEdit}
				canAdd={canAdd}
				onDuplicateCheckpoint={duplicateCheckpoint}
				onExportAttendeesList={exportAttendeesList}
				onChangeCheckpointsStatus={changeCheckpointsStatus}
				onCreateExport={createExport}
				pagination={pagination}
				loading={loading}
			/>
		</>
	);
};

CheckpointsList.propTypes = {
	checkpoints: PropTypes.arrayOf(PropTypes.instanceOf(Checkpoint)).isRequired,
	changeCheckpointsStatus: PropTypes.func.isRequired,
	exportAttendeesList: PropTypes.func.isRequired,
	createExport: PropTypes.func.isRequired,
	duplicateCheckpoint: PropTypes.func.isRequired,
	toggleOrdering: PropTypes.func.isRequired,
	ordering: PropTypes.shape({
		column: PropTypes.string,
		direction: PropTypes.oneOf(["asc", "desc"])
	}).isRequired,
	organization: PropTypes.object.isRequired,
	eventId: PropTypes.object.isRequired,
	selectedCheckpointIds: PropTypes.shape({
		value: PropTypes.arrayOf(PropTypes.any).isRequired,
		onChange: PropTypes.func.isRequired
	}).isRequired,
	pageSize: PropTypes.shape({
		value: PropTypes.number,
		onChange: PropTypes.func.isRequired
	}).isRequired,
	loading: PropTypes.bool,
	pagination: PropTypes.object.isRequired,
	refetch: PropTypes.func
};

CheckpointsList.defaultProps = {
	loading: false,
	refetch: () => {}
};

export default CheckpointsList;
